<template>
  <validation-observer v-slot="{ invalid }" ref="formRef" class="form form-sm mx-auto">
    <slot name="header">
      <h4 class="mb-4">Request</h4>
    </slot>
    <b-form @submit.prevent="handleSubmit">
      <base-input
        v-model="formData.first_name"
        rules="required"
        name="Name"
        label="Name"
      ></base-input>

      <base-input
        v-model="formData.company"
        rules="required"
        name="Company"
        label="Company"
      ></base-input>

      <base-input
        v-model="formData.email"
        rules="required|email"
        name="Email Address"
        label="Email Address"
        :disabled="'' !== formData.email"
      ></base-input>

      <validation-provider name="terms" rules="is:accepted">
        <b-form-checkbox
          id="terms"
          v-model="formData.terms"
          name="terms"
          value="accepted"
          unchecked-value="not_accepted"
        >
          By selecting this box, I state that I have read, understand, and agree to be
          bound by the <a
            @click.prevent.stop="() => (showTCModal = true)"
            class="text-underline"
            href
            >Terms of Service</a
          > and <a
            @click.prevent.stop="() => (showPPModal = true)"
            class="text-underline"
            href
            >Privacy Policy</a
          >.
        </b-form-checkbox>
      </validation-provider>

      <VueRecaptcha
        ref="recaptchaRef"
        @verify="onCaptchaVerify"
        @expired="onCaptchaExpired"
        :sitekey="recaptchaKey"
        class="mt-3 d-flex justify-content-center"
        load-recaptcha-script
      />

      <base-button
        :disabled="invalid || !isVerified"
        :loading="isBusy"
        type="primary"
        native-type="submit"
        size="lg"
        class="mt-4"
        block
      >
        <slot name="submit-text">Sign Up</slot>
      </base-button>
    </b-form>

    <ModalTermsAndConditions v-model="showTCModal" />
    <ModalPrivacyPolicy v-model="showPPModal" />
  </validation-observer>
</template>

<script>
import Vue from "vue";
import {
  defineComponent,
  computed,
  reactive,
  ref,
  watch,
  onUnmounted,
} from "@vue/composition-api";
import { useRouter } from "vue2-helpers/vue-router";
import VueRecaptcha from "vue-recaptcha";

import { AuthenticationService, GRecaptchaService } from "@services";

import ModalTermsAndConditions from "@components/ModalTermsAndConditions.vue";
import ModalPrivacyPolicy from "@components/ModalPrivacyPolicy.vue";

export default defineComponent({
  name: "FormSignupRequest",
  components: {
    VueRecaptcha,
    ModalTermsAndConditions,
    ModalPrivacyPolicy,
  },
  setup() {
    const router = useRouter();

    const formRef = ref();
    const recaptchaRef = ref();
    const isBusy = ref(false);
    const isVerified = ref(false);
    const showTCModal = ref(false);
    const showPPModal = ref(false);

    const formData = reactive({
      email: AuthenticationService.requestAccessEmail,
    });

    const recaptchaKey = computed(() => process.env.VUE_APP_RECAPTCHA_KEY);

    const onCaptchaVerify = async (response) => {
      const {
        data: {
          data: { status },
        },
      } = await GRecaptchaService.verifyResponse(response).catch((err) => {
        Vue.prototype.$toasted.show("Recaptcha not verified", {
          type: "error",
        });

        recaptchaRef.value.reset();

        return Promise.reject(err);
      });

      isVerified.value = status;
    };

    const onCaptchaExpired = () => (isVerified.value = false);

    const handleSubmit = async () => {
      isBusy.value = true;

      const {
        data: {
          data: { message },
        },
      } = await AuthenticationService.signup(formData);

      formRef.value.reset();
      isBusy.value = false;

      Vue.prototype.$toasted.show(message);
      Vue.prototype.$bus.$emit("signup-request-form-success", true);

      router.push({
        name: "Home",
      });
    };

    watch(
      () => isBusy.value,
      (newIsBusy) => {
        Vue.prototype.$bus.$emit("signup-request-form-processing", newIsBusy);
      }
    );

    onUnmounted(() => AuthenticationService.setRequestAccessEmail(null));

    return {
      formRef,
      recaptchaRef,
      isBusy,
      isVerified,
      showTCModal,
      showPPModal,
      formData,
      recaptchaKey,
      onCaptchaVerify,
      onCaptchaExpired,
      handleSubmit,
    };
  },
});
</script>
